/**
 * Remember which art URLs 404'd so we never re-request them.
 * Missing plates are normal (not every verse ships art yet); hammering
 * the network on every hand/board rebuild floods the console.
 */

const missing = new Set<string>();
const ok = new Set<string>();

export function isArtKnownMissing(src: string): boolean {
  return missing.has(src);
}

export function isArtKnownOk(src: string): boolean {
  return ok.has(src);
}

export function markArtMissing(src: string): void {
  missing.add(src);
  ok.delete(src);
}

export function markArtOk(src: string): void {
  ok.add(src);
  missing.delete(src);
}

/**
 * Wire a card/prayer/lie image: skip network if already known missing,
 * record success/failure once, call onMissing when the plate is gone.
 */
export function bindArtImage(
  img: HTMLImageElement,
  src: string,
  onMissing: () => void,
): void {
  if (isArtKnownMissing(src)) {
    onMissing();
    return;
  }

  // Avoid re-assigning the same src (can re-trigger load / console spam)
  const current = img.getAttribute("src");
  if (current === src) {
    if (img.complete && img.naturalWidth === 0) {
      markArtMissing(src);
      onMissing();
    } else if (img.complete && img.naturalWidth > 0) {
      markArtOk(src);
    }
    return;
  }

  img.addEventListener(
    "error",
    () => {
      markArtMissing(src);
      onMissing();
    },
    { once: true },
  );
  img.addEventListener(
    "load",
    () => {
      markArtOk(src);
    },
    { once: true },
  );
  img.src = src;
}
